import { Modal } from 'antd'
import ModalStyledLoader from 'components/Elements/ModalStyledLoader'
import StyledTooltip from 'components/Elements/StyledTooltip'
import { useState } from 'react'
import { Button, Col, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { FaEnvelope } from 'react-icons/fa'
import 'react-quill/dist/quill.snow.css'
import { useMediaQuery } from 'react-responsive'
import { useParams } from 'react-router'
import axiosClient from 'services/axios-client'
import { breakpoints } from 'style'

import { ContentState, EditorState, convertToRaw } from 'draft-js'
import draftToHtml from 'draftjs-to-html'
import htmlToDraft from 'html-to-draftjs'
import { Editor } from 'react-draft-wysiwyg'
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css'

import { useQueryClient } from '@tanstack/react-query'
import { useAtom } from 'jotai'
import { activeAnswerAtom, activeQuestionAtom } from 'store/atoms'
import { useBookRoleQuery } from 'store/queries'
import Images from './components/Images'

function AnswerModal({ show, onHide, onAnswerChange = () => {} }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [answer, setAnswer] = useAtom(activeAnswerAtom)
  const [question] = useAtom(activeQuestionAtom)
  const { bookId } = useParams()
  const { data: role } = useBookRoleQuery(bookId)
  const [isSavingDraft, setIsSavingDraft] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showImagesTab, setShowImagesTab] = useState(false)
  const [allowPublish, setAllowPublish] = useState(true)
  const [showShareSuccess, setShowShareSuccess] = useState(false)
  const [showDraftSuccess, setShowDraftSuccess] = useState(false)

  const contentDataState = ContentState.createFromBlockArray(
    htmlToDraft(`${answer?.answer_text || ''}`),
  )

  const editorDataState = EditorState.createWithContent(contentDataState)

  const [editorState, setEditorState] = useState(editorDataState)

  const onEditorStateChange = async (editorStateData) => {
    setEditorState(editorStateData)
  }

  const htmlOfEditor = draftToHtml(
    convertToRaw(editorState.getCurrentContent()),
  )

  const isMobile = useMediaQuery({
    query: `(max-width: ${breakpoints.sm}px)`,
  })

  const onCloseModalAnswer = () => {
    onHide()
    setAnswer(null)
  }

  const handleSaveDraft = async () => {
    setIsSavingDraft(true)
    const postData = {
      book_id: bookId,
      question_id: question?.id,
      book_question_join_id: question?.book_question_join_id,
      user_id: role?.user_id,
      answer_text: htmlOfEditor,
      is_submitted: 0,
      allow_publish: allowPublish === true ? 1 : 0,
      authorFirstName: answer?.first_name || role?.first_name,
      authorLastName: answer?.last_name || role?.last_name,
    }

    let answerResult = {}

    if (!answer) {
      answerResult = await axiosClient.post(`/answer`, postData)
    } else {
      answerResult = await axiosClient.put(`/answer/${answer.id}`, postData)
    }

    if (answerResult.ok) {
      queryClient.invalidateQueries({ queryKey: ['book', bookId, 'questions'] })

      setIsSavingDraft(false)
      setShowDraftSuccess(true)

      const newAnswer = {
        ...answer,
        ...postData,
        ...answerResult.data,
      }
      onAnswerChange(newAnswer)

      return newAnswer
    } else {
      // TODO: Handle error case.
      setIsSavingDraft(false)
    }
  }

  const handleSubmit = async () => {
    let postData = {}
    let answerResult = {}

    setIsSubmitting(true)

    postData = {
      book_id: bookId,
      question_id: question?.id,
      book_question_join_id: question?.book_question_join_id,
      user_id: role?.user_id,
      answer_text: htmlOfEditor,
      is_submitted: 1,
      allow_publish: allowPublish === true ? 1 : 0,
      authorFirstName: answer?.first_name || role?.first_name,
      authorLastName: answer?.last_name || role?.last_name,
    }
    if (answer) {
      answerResult = await axiosClient.put(`/answer/${answer.id}`, postData)
    } else {
      answerResult = await axiosClient.post(`/answer`, postData)
    }

    if (answerResult.ok) {
      queryClient.invalidateQueries({ queryKey: ['book', bookId, 'questions'] })
      queryClient.invalidateQueries({ queryKey: ['book', bookId, 'stats'] })

      setIsSubmitting(false)
      setShowShareSuccess(true)
      const newAnswer = {
        ...answer,
        ...postData,
        ...answerResult.data,
      }
      onAnswerChange(newAnswer)
    }
  }

  const onClickDiscard = () => {
    const contentDataState = ContentState.createFromBlockArray(
      htmlToDraft(`${answer?.answer_text || ''}`),
    )

    setEditorState(EditorState.createWithContent(contentDataState))
  }

  const clickPicture = () => {
    setShowImagesTab(true)
  }

  const clickStory = () => {
    setShowImagesTab(false)
  }

  return (
    <Modal
      title={t('tt_edit_story')}
      visible={show}
      onCancel={onCloseModalAnswer}
      footer={false}
      maskClosable={false}
      wrapClassName="modal-answer"
      getContainer={() => document.getElementById('story-section')}
    >
      <div className="card card-header mb-20">
        <div className="progressbar-container">
          <div className="progressbar-tab">
            <div
              className={`${showImagesTab ? '' : 'active'} tab-story`}
              onClick={clickStory}
            >
              <strong>{t('story')}</strong>
            </div>
            <div
              className={`${showImagesTab ? 'active' : ''} tab-picture`}
              onClick={clickPicture}
            >
              <strong>{t('pictures')}</strong>
            </div>
          </div>
        </div>
      </div>

      {showImagesTab ? (
        <Images
          bookId={bookId}
          isMobile={isMobile}
          onSaveDraft={handleSaveDraft}
        />
      ) : (
        <>
          <div className="answer-content-wrapper modal-custom-body editor p20">
            <div className={`answer-info-${isMobile ? 'sm' : 'md'}`}>
              <div className="answer-content">
                <h4>{question?.dsply_text}</h4>

                {!showDraftSuccess && !showShareSuccess && (
                  <Editor
                    editorState={editorState}
                    onEditorStateChange={onEditorStateChange}
                    wrapperClassName="wrapperClassName"
                    editorClassName="editorClassName"
                    toolbarClassName="toolbar-class"
                    placeholder="Start typing your story here."
                    toolbar={{
                      options: ['inline', 'list', 'textAlign'],
                      inline: {
                        options: [
                          'bold',
                          'italic',
                          'underline',
                          'strikethrough',
                        ],
                      },
                      list: {
                        options: [], //options: ['unordered', 'ordered', 'indent', 'outdent'],
                      },
                      textAlign: {
                        options: [], //options: ['left', 'center', 'right'],
                      },
                    }}
                  />
                )}
              </div>
            </div>
            <div className="d-flex align-items-center justify-content-center mt-auto mb-auto">
              {showDraftSuccess && <h3>Your changes have been saved.</h3>}

              {showShareSuccess && (
                <h3>Congratulations, your story has been shared!</h3>
              )}
            </div>
          </div>

          <div className="answer-content-wrapper modal-custom-footer">
            <Row>
              <Col md={{ span: 2 }}>
                {(role?.name === 'Purchaser' || role?.name === 'Editor') && (
                  <>
                    <input
                      type="checkbox"
                      checked={allowPublish}
                      onChange={(evt) => setAllowPublish(evt.target.checked)}
                    />{' '}
                    {t('include_in_book')}
                  </>
                )}
              </Col>
              <Col md={{ span: 10 }}>
                <div className="btns-content-wrapper actions text-right">
                  {showDraftSuccess || showShareSuccess ? (
                    <>
                      <Button
                        className="btn-blue btn-dash mr-3"
                        onClick={() => {
                          setShowDraftSuccess(false)
                          setShowShareSuccess(false)
                        }}
                      >
                        Continue Editing
                      </Button>

                      <Button
                        className="btn-white btn-dash mr-3"
                        onClick={() => {
                          setShowDraftSuccess(false)
                          setShowShareSuccess(false)
                          onCloseModalAnswer()
                        }}
                      >
                        Exit
                      </Button>
                    </>
                  ) : (
                    <>
                      {answer && answer?.answer_text !== htmlOfEditor && (
                        <Button
                          className="btn-white btn-dash mr-3"
                          disabled={
                            editorState === answer?.answer_text ||
                            isSavingDraft ||
                            isSubmitting
                          }
                          onClick={onClickDiscard}
                        >
                          {t('discard_changes')}
                        </Button>
                      )}
                      <Button
                        className="btn-blue btn-dash mr-3"
                        onClick={handleSaveDraft}
                        disabled={
                          answer?.answer_text === htmlOfEditor ||
                          isSavingDraft ||
                          isSubmitting
                        }
                        style={{ width: '110px' }}
                      >
                        {isSavingDraft ? (
                          <ModalStyledLoader small />
                        ) : (
                          <>{t('save')}</>
                        )}
                      </Button>
                      {!answer?.is_submitted && showImagesTab === false && (
                        <Button
                          className="btn-blue btn-dash m15"
                          onClick={handleSubmit}
                          disabled={isSavingDraft || isSubmitting}
                        >
                          {isSubmitting ? (
                            <ModalStyledLoader small />
                          ) : (
                            <StyledTooltip
                              title={t('save_and_submit_story')}
                              placement="top"
                            >
                              <FaEnvelope className="mr-3" />
                              {t('save_and_submit_story')}
                            </StyledTooltip>
                          )}
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </Col>
            </Row>
          </div>
        </>
      )}
    </Modal>
  )
}

export default AnswerModal
